// Orchestration for the ADMIN "Create invoice" feature. The pure rules live in adminInvoicePure.js, the
// Supabase-first insert in supabase.js (createAdminPortalOrder); this file is the sequence that ties
// them together, written in the injectable-deps style of adminAdjust.js so the whole thing is testable
// with fakes.
//
// SAVE SEQUENCE (performCreateAdminInvoice), strictly in this order:
//   0. validate the draft (pure, no I/O)
//   1. FRESH strict reads in parallel: the dealer profile, the dealer's customer_settings, the live
//      product catalog, and (when a rep is credited) that rep's profile. Any read failing = nothing is saved.
//   2. guards on the fresh data: the dealer is still an approved dealer; the account discount is still the
//      one the admin was shown (unless the admin typed their own); every catalog price / SKU is still what
//      the admin saw; the credited rep is still an active rep; an email address exists when one is wanted
//   3. build the portal_orders payload from FRESH values only
//   4. createAdminPortalOrder (awaited). On ANY failure NOTHING is written to SHARED_INVOICES or
//      localStorage and nothing is emailed.
//   5. only after a verified insert: mirror into SHARED_INVOICES (add-if-absent by id, enrich the thin copy
//      the admin's own realtime echo may already have added)
//   6. only then the best-effort dealer email, reported honestly (sent only when success === true)
import { SHARED_INVOICES } from "./RepPortal";
import { fetchFreshProfile, getCustomerSettingsStrict, getPortalProductsStrict, createAdminPortalOrder, findPortalOrderByRefStrict, isAdminInsertInFlight } from "./supabase";
import { emailInvoice } from "./email";
import { isUuid, samePct, formatPct, round2 } from "./invoiceDiscount";
import {
  isAdminInvoiceId, freshProfileProblem, freshRepProblem, repStamp, accountRateFrom, validatePercentInput, validEmail,
  termsOptionsFor, defaultTermsId, termsLabelForId, buildCatalogLookup, buildAdminInvoiceItems, catalogDrift,
  computeAdminPricing, pricingProblem, buildPortalOrderPayload, buildCacheInvoice, buildInvoiceEmailPayload, repDisplayName, HOUSE_REP_LABEL, storedViewFromRow, ADMIN_INVOICE_SOURCE,
} from "./adminInvoicePure";

const defaultDeps = { fetchFreshProfile, getCustomerSettingsStrict, getPortalProductsStrict, createAdminPortalOrder, findPortalOrderByRefStrict, isAdminInsertInFlight, SHARED_INVOICES, emailInvoice, now: () => new Date() };

export const READ_TIMEOUT_MS = 15000;
export const EMAIL_TIMEOUT_MS = 30000;

// Runs one read; never rejects. -> { ok:true, value } | { ok:false, error }
async function safeRead(fn, label, ms) {
  let timer;
  try {
    const timeout = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(label + " timed out after " + Math.round(ms / 1000) + " seconds")), ms); });
    const value = await Promise.race([Promise.resolve().then(fn), timeout]);
    if (value && typeof value === "object" && value.ok === false) {
      console.warn("[adminCreateInvoice] " + label + " failed:", value.error);
      return { ok: false, error: value.error || (label + " failed") };
    }
    return { ok: true, value };
  } catch (e) {
    console.warn("[adminCreateInvoice] " + label + " failed:", e?.message || e);
    return { ok: false, error: e?.message || (label + " failed") };
  } finally { clearTimeout(timer); }
}

/**
 * Read the dealer FRESH (profile + customer_settings, both strict) and work out everything the form pre-fills.
 * @returns {Promise<{ok:true, profile, settings, rate, terms:{options,fallback,defaultId}, creditLimit, assignedRep}|{ok:false, error}>}
 */
export async function loadAdminInvoiceAccount(dealerId, allTerms, deps = defaultDeps) {
  const d = { ...defaultDeps, ...deps };
  if (!isUuid(String(dealerId || ""))) return { ok: false, error: "This account has no valid account id." };
  const [p, s] = await Promise.all([
    safeRead(() => d.fetchFreshProfile(dealerId), "The dealer account read", READ_TIMEOUT_MS),
    safeRead(() => d.getCustomerSettingsStrict(dealerId), "The account settings read", READ_TIMEOUT_MS),
  ]);
  if (!p.ok) return { ok: false, error: "The dealer account could not be read (" + p.error + ")." };
  if (!s.ok) return { ok: false, error: "The account's discount, terms and rep could not be read (" + s.error + ")." };
  const profile = p.value;
  const settings = s.value.settings || {};
  const problem = freshProfileProblem(profile, dealerId);
  if (problem) return { ok: false, error: problem };
  const rate = accountRateFrom(profile.discount, settings.discount, !!s.value.found);
  if (!rate.ok) return { ok: false, error: rate.error };
  const t = termsOptionsFor(settings.terms, profile.terms, allTerms);
  const creditLimit = Number(profile.credit_limit) || Number(settings.creditLimit) || 0;
  const assignedRep = String(settings.rep || "").trim() || String(profile.rep || "").trim();
  return { ok: true, profile, settings, settingsFound: !!s.value.found, rate, terms: { options: t.options, fallback: t.fallback, defaultId: defaultTermsId(t.options) }, creditLimit, assignedRep };
}

const moneyText = (n) => "$" + round2(n).toFixed(2);

function driftMessage(drift) {
  const parts = drift.map((x) => (x.now == null
    ? x.sku + " is no longer in the product catalog"
    : x.sku + " is now " + moneyText(x.now) + (x.was == null ? "" : " (it showed " + moneyText(x.was) + ")")));
  return "The product catalog changed while this window was open: " + parts.join("; ") + ". The prices on screen have been updated. Review the total, then press the button again. Nothing was saved.";
}

/** Add-if-absent by id; when the admin's own realtime echo already added a thin copy, merge only the
 *  richer fields into it (userId, customerInfo, ...) instead of adding a duplicate. -> "added" | "merged" | "failed" */
export function mirrorAdminInvoice(cache, invoice) {
  try {
    const existing = (cache.getAll() || []).find((i) => i && i.id === invoice.id);
    if (!existing) { cache.add(invoice); return "added"; }
    const patch = {};
    ["userId", "customerEmail", "customerInfo", "orderId", "paymentTerms", "subtotal", "discount", "source"].forEach((k) => {
      const have = existing[k];
      const missing = have == null || have === "" || (k === "customerInfo" && typeof have === "object" && !Object.keys(have).length);
      if (missing && invoice[k] != null && invoice[k] !== "") patch[k] = invoice[k];
    });
    if (Object.keys(patch).length) cache.updateShipping(invoice.id, patch);
    return "merged";
  } catch (e) {
    console.warn("[adminCreateInvoice] the invoice IS saved in the database, but updating this device's invoice list failed:", e?.message || e);
    return "failed";
  }
}

/** Best-effort email of a created invoice to the dealer. Never throws. Honest outcome:
 *  { status:"sent", to } only when the email service answered success === true, otherwise { status:"failed", error }. */
export async function sendAdminInvoiceEmail(invoice, mpc, deps = defaultDeps) {
  const d = { ...defaultDeps, ...deps };
  const to = String(invoice && invoice.customerEmail || "").trim();
  if (!validEmail(to)) return { status: "failed", to, error: "There is no valid email address on file for this dealer." };
  let timer;
  try {
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve({ success: false, error: "The email service did not answer within " + Math.round(EMAIL_TIMEOUT_MS / 1000) + " seconds, so it is not confirmed. It may still arrive: check before sending again." }), EMAIL_TIMEOUT_MS); });
    const res = await Promise.race([Promise.resolve(d.emailInvoice(buildInvoiceEmailPayload({ invoice, mpc }))), timeout]);
    if (res && res.success === true) {
      // The email service answered success, but api/send-email.js sends the email WITHOUT the invoice PDF when
      // the PDF could not be generated (pdfAttached:false): that is not a full "Emailed".
      if (res.pdfAttached === false) {
        console.warn("[adminCreateInvoice] invoice email for", invoice.id, "was sent WITHOUT the PDF attached");
        return { status: "sent_no_pdf", to };
      }
      return { status: "sent", to };
    }
    console.warn("[adminCreateInvoice] invoice email was NOT sent for", invoice.id, ":", res && res.error);
    return { status: "failed", to, error: (res && res.error) || "The email service did not confirm the send." };
  } catch (e) {
    console.warn("[adminCreateInvoice] invoice email threw for", invoice.id, ":", e?.message || e);
    return { status: "failed", to, error: e?.message || "The email could not be sent." };
  } finally { clearTimeout(timer); }
}

/** "Resend" from the success screen. */
export async function resendAdminInvoiceEmail({ invoice, mpc }, deps = defaultDeps) {
  return sendAdminInvoiceEmail(invoice, mpc, deps);
}

/**
 * Create an invoice for a dealer from the admin panel.
 * @param {{invoiceId:string, dealerId:string, lines:Array<{sku:string,qty:number|string}>, pctInput:string|number,
 *   shownAccountPct:number, shownBySku:object, repChoice:({mode:"rep",id:string}|{mode:"house"}|null),
 *   paymentTermsId:string, emailDealer:boolean, mpc:object, afterUncertain?:boolean, afterGateway?:boolean,
 *   onWait?:(ms:number)=>void}} p
 * @returns {Promise<{ok:false, code:string, message:string, uncertain:boolean, freshRate?:object, freshCatalog?:Array}|
 *   {ok:true, invoice, cacheInvoice, pricing, row, deduped:boolean, mirrored:string, email:{status:string,to?:string,error?:string}}>}
 */
export async function performCreateAdminInvoice(p, deps = defaultDeps) {
  const d = { ...defaultDeps, ...deps };
  const fail = (code, message, extra) => ({ ok: false, code, message, uncertain: false, ...(extra || {}) });
  const { invoiceId, dealerId, lines, pctInput, shownAccountPct, shownBySku, repChoice, paymentTermsId, emailDealer, mpc, afterUncertain, afterGateway, onWait } = p || {};

  // 0 ── validate the draft (no I/O)
  if (!isAdminInvoiceId(invoiceId)) return fail("input", "The invoice number is not valid. Close this window and open it again.");
  if (!isUuid(String(dealerId || ""))) return fail("input", "Choose the dealer to invoice.");
  if (!repChoice || (repChoice.mode !== "house" && !(repChoice.mode === "rep" && isUuid(String(repChoice.id || ""))))) {
    return fail("input", "Choose who is credited with this invoice: a sales rep, or No rep (house account).");
  }
  const termsLabel = termsLabelForId(paymentTermsId);
  if (!termsLabel) return fail("input", "Choose the payment terms.");
  const pctRes = validatePercentInput(pctInput);
  if (!pctRes.ok) return fail("input", pctRes.error);
  if (!Array.isArray(lines) || lines.length === 0) return fail("input", "Add at least one item.");

  // 1 ── fresh strict reads, all at once
  const wantRep = repChoice.mode === "rep";
  const [prof, sett, cat, repR] = await Promise.all([
    safeRead(() => d.fetchFreshProfile(dealerId), "The dealer account read", READ_TIMEOUT_MS),
    safeRead(() => d.getCustomerSettingsStrict(dealerId), "The account settings read", READ_TIMEOUT_MS),
    safeRead(() => d.getPortalProductsStrict(), "The product catalog read", READ_TIMEOUT_MS),
    wantRep ? safeRead(() => d.fetchFreshProfile(repChoice.id), "The sales rep account read", READ_TIMEOUT_MS) : Promise.resolve({ ok: true, value: null }),
  ]);
  if (!prof.ok) return fail("read", "Could not re-check the dealer account (" + prof.error + "). Nothing was saved.");
  if (!sett.ok) return fail("read", "Could not re-check the account's discount and terms (" + sett.error + "). Nothing was saved.");
  if (!cat.ok) return fail("read", "Could not re-check the product prices (" + cat.error + "). Nothing was saved.");
  if (!repR.ok) return fail("read", "Could not re-check the sales rep account (" + repR.error + "). Nothing was saved.");
  const profile = prof.value;
  const settings = (sett.value && sett.value.settings) || {};

  // 2 ── guards on the fresh data
  const accountProblem = freshProfileProblem(profile, dealerId);
  if (accountProblem) return fail("account", accountProblem + " Nothing was saved.");
  const rate = accountRateFrom(profile.discount, settings.discount, !!(sett.value && sett.value.found));
  if (!rate.ok) return fail("rate_invalid", rate.error + " Nothing was saved.", { freshRate: null });
  const overridden = !samePct(pctRes.pct, shownAccountPct);
  if (!overridden && !samePct(rate.pct, shownAccountPct)) {
    return fail("rate_changed", "The account's discount is now " + (rate.pct > 0 ? formatPct(rate.pct) + "%" : "none") + "; it showed " + (Number(shownAccountPct) > 0 ? formatPct(shownAccountPct) + "%" : "none") + " when this window loaded. Review the total, then press the button again. Nothing was saved.", { freshRate: rate });
  }
  if (emailDealer && !validEmail(profile.email)) {
    return fail("no_email", "This dealer has no valid email address on file, so the invoice cannot be emailed. Use Create invoice (save only), or add an email in Edit account first. Nothing was saved.");
  }
  const fresh = buildCatalogLookup(cat.value.products, cat.value.kits);
  const drift = catalogDrift(lines, shownBySku, fresh.bySku);
  if (drift.length) return fail("catalog_changed", driftMessage(drift), { freshCatalog: fresh.list, drift });
  const built = buildAdminInvoiceItems(lines, fresh.bySku);
  if (!built.ok) return fail("items", built.problems.map((x) => x.reason).join(" ") + " Nothing was saved.");
  const pricing = computeAdminPricing(built.items, pctRes.pct);
  const priceProblem = pricingProblem(pricing);
  if (priceProblem) return fail("pricing", priceProblem + " Nothing was saved.");
  if (wantRep) {
    const repProblem = freshRepProblem(repR.value, repChoice.id);
    if (repProblem) return fail("rep", repProblem + " Choose another rep or the house account. Nothing was saved.");
  }
  const stamp = repStamp(repChoice, repR.value);
  if (!stamp.ok) return fail("rep", stamp.error + " Nothing was saved.");

  // 3 ── payload from FRESH values only
  const payload = buildPortalOrderPayload({ invoiceId, profile, rep: stamp.stamp, paymentTerms: termsLabel, items: built.items, pricing });

  // 4 ── the awaited insert. A failure leaves NOTHING local.
  //      afterUncertain: this is a retry of an attempt whose outcome was not confirmed, so the insert layer
  //      waits and looks the invoice number up again before it may insert anything.
  let res;
  //      afterGateway: that earlier result was a gateway / 5xx / dropped connection (not our own timeout-and-abort), so the
  //      wait is the long one. onWait(ms) lets the window show a countdown while the insert layer waits.
  try { res = await d.createAdminPortalOrder(payload, { ...(d.orderOpts || {}), afterUncertain: !!afterUncertain, afterGateway: !!afterGateway, onWait }); }
  catch (e) {
    console.warn("[adminCreateInvoice] createAdminPortalOrder threw for", invoiceId, ":", e?.message || e);
    return { ok: false, code: "network", uncertain: true, message: "The connection dropped while saving. Invoice " + invoiceId + " MAY have been created: open Orders and look for " + invoiceId + " before trying again." };
  }
  if (!res || res.ok !== true) {
    return { ok: false, code: (res && res.code) || "insert_failed", uncertain: !!(res && res.uncertain), ownAbort: !!(res && res.ownAbort), message: (res && res.message) || "The invoice could not be saved. Nothing was saved." };
  }

  // 5-6 ── mirror into this device's cache, only now, then the best-effort email (shared with the "Check the database now" path)
  return finishSavedInvoice({ invoiceId, profile, row: res.row, deduped: !!res.deduped, view: { paymentTerms: termsLabel, items: built.items, pricing, rep: stamp.stamp }, emailDealer, mpc, d });
}

/**
 * The tail of a save that the database has CONFIRMED (a fresh insert, a deduped retry, or a row found by the
 * "Check the database now" button): mirror into this device's cache, then the optional best-effort email.
 * On a DEDUPED save / a found row the invoice is built from the STORED row (its terms, items, totals, email and
 * rep), never from the new attempt's values.
 */
async function finishSavedInvoice({ invoiceId, profile, row, deduped, view: given, emailDealer, mpc, d }) {
  let view = { paymentTerms: given.paymentTerms, items: given.items, pricing: given.pricing, rep: given.rep, customerEmail: undefined };
  if (deduped) {
    const st = storedViewFromRow(row, view);
    view = { paymentTerms: st.paymentTerms, items: st.items, pricing: st.pricing, rep: st.rep, customerEmail: st.customerEmail };
  }
  const invoice = buildCacheInvoice({ invoiceId, profile, rep: view.rep, paymentTerms: view.paymentTerms, items: view.items, pricing: view.pricing, row, now: d.now(), customerEmail: view.customerEmail });
  const mirrored = mirrorAdminInvoice(d.SHARED_INVOICES, invoice);
  let live = invoice;
  try { live = (d.SHARED_INVOICES.getAll() || []).find((i) => i && i.id === invoice.id) || invoice; }
  catch (e) { console.warn("[adminCreateInvoice] reading the cached copy of", invoice.id, "failed; using the freshly built copy:", e?.message || e); live = invoice; }

  // best-effort email, reported honestly
  let email = { status: "not_requested" };
  if (emailDealer) email = await sendAdminInvoiceEmail(invoice, mpc, d);

  return { ok: true, invoice, cacheInvoice: live, pricing: view.pricing, row, deduped: !!deduped, mirrored, email, repLabel: view.rep, repName: repDisplayName(view.rep) };
}

/**
 * "Check the database now": after a save whose outcome was NOT confirmed, look the invoice number up in
 * portal_orders itself (strict, no cache, no realtime) instead of trusting the Orders list, which may be out of
 * date after a connection problem. Never throws, never writes to the database.
 *  found      -> the invoice IS saved: the success result is built from the STORED row (cache mirror, optional email),
 *                exactly like a deduped success. { ok:true, state:"found", result }
 *  not_found  -> { ok:true, state:"not_found" }  (or state:"in_flight" while this page's own insert has not settled)
 *  could not check / unexpected -> { ok:false, state, message }: this is NEVER "not there".
 * @param {{invoiceId:string, emailDealer?:boolean, mpc?:object}} p
 */
export async function checkSavedAdminInvoice(p, deps = defaultDeps) {
  const d = { ...defaultDeps, ...deps };
  const { invoiceId, emailDealer, mpc } = p || {};
  if (!isAdminInvoiceId(invoiceId)) return { ok: false, state: "could_not_check", message: "The invoice number is not valid, so the database could not be checked." };
  let look;
  try { look = await d.findPortalOrderByRefStrict(invoiceId); }
  catch (e) {
    console.warn("[adminCreateInvoice] the database check for", invoiceId, "threw:", e?.message || e);
    look = { ok: false, error: e?.message || "connection problem" };
  }
  if (!look || look.ok !== true) {
    return { ok: false, state: "could_not_check", message: "Could not check the database (" + ((look && look.error) || "no answer") + "). That does NOT mean the invoice is missing. Try again, or reload the page and look in Orders." };
  }
  if (!look.found) {
    let inFlight = false;
    try { inFlight = !!d.isAdminInsertInFlight(invoiceId); }
    catch (e) { console.warn("[adminCreateInvoice] reading the in-flight state of", invoiceId, "failed:", e?.message || e); }
    return { ok: true, state: inFlight ? "in_flight" : "not_found" };
  }
  const row = look.row || {};
  if (String(row.invoice_ref == null ? "" : row.invoice_ref).trim() !== invoiceId || String(row.source || "") !== ADMIN_INVOICE_SOURCE) {
    console.warn("[adminCreateInvoice] a row with invoice_ref", invoiceId, "exists but is not an office invoice created here:", { id: row.id, source: row.source, invoice_ref: row.invoice_ref });
    return { ok: false, state: "unexpected_row", message: "A row numbered " + invoiceId + " exists in the database but it is not an office invoice made in this window. Open Orders and look at it before doing anything else." };
  }
  const stored = storedViewFromRow(row, {});
  const totalOk = row.total != null && Number.isFinite(Number(row.total)) && Number(row.total) > 0;
  if (!totalOk || !Array.isArray(stored.items) || stored.items.length === 0 || !stored.pricing) {
    console.warn("[adminCreateInvoice] the stored row for", invoiceId, "exists but its total or items could not be read:", { total: row.total, items: row.items });
    return { ok: true, state: "found_unreadable", total: row.total, customer: row.customer || "" };
  }
  // The dealer's profile only fills the address / phone / EIN lines of the cached copy and the PDF: best effort,
  // and the invoice already exists, so a failed read never blocks it (the stored name and email are used instead).
  let profile = null;
  if (isUuid(String(row.user_id || ""))) {
    const pr = await safeRead(() => d.fetchFreshProfile(row.user_id), "The dealer account read", READ_TIMEOUT_MS);
    if (pr.ok && pr.value && pr.value.id) profile = pr.value;
    else console.warn("[adminCreateInvoice] could not read the dealer profile for the stored invoice", invoiceId, "- using the stored name and email:", pr.error);
  }
  if (!profile) profile = { id: row.user_id, company: row.customer || "", email: row.customer_email || "", address: "", phone: "", ein: "" };
  else profile = { ...profile, company: row.customer || profile.company, email: profile.email || row.customer_email || "" };
  const result = await finishSavedInvoice({ invoiceId, profile, row, deduped: true, view: { paymentTerms: undefined, items: undefined, pricing: undefined, rep: undefined }, emailDealer: !!emailDealer, mpc, d });
  return { ok: true, state: "found", result: { ...result, fromCheck: true } };
}
